import {DialogPrimitive as Dialog} from "ch3-ui-lib";
import {PhoneHQButton} from "ch3-ui-lib";
import {X} from "lucide-react";
import type {ReactNode} from "react";

const lucide = '!p-0 shrink-0 !text-current';

/** primary: brand. danger: filled red (delete). leave: secondary with red text ("Leave" in Unsaved changes). */
export type ConfirmTone = 'primary' | 'danger' | 'leave';

const TONE: Record<ConfirmTone, { variant: 'primary' | 'secondary'; className: string }> = {
    primary: {variant: 'primary', className: ''},
    danger: {variant: 'primary', className: '!border-danger !bg-danger hover:!border-danger-hover hover:!bg-danger-hover'},
    leave: {variant: 'secondary', className: '!text-danger-ink'},
};

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    children: ReactNode;
    cancelLabel: string;
    confirmLabel: string;
    tone?: ConfirmTone;
    busy?: boolean;
    onConfirm: () => void;
    onCloseAutoFocus?: (event: Event) => void;
}

/** 400px confirm dialog (Queues-Unsaved, SettingsPhone-Unsaved, delete confirmations). */
export const ConfirmDialog = ({open, onOpenChange, title, children, cancelLabel, confirmLabel, tone = 'primary', busy, onConfirm, onCloseAutoFocus}: ConfirmDialogProps) => (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[60] bg-scrim"/>
            <Dialog.Content
                role="alertdialog"
                aria-describedby="confirm-dialog-desc"
                onCloseAutoFocus={onCloseAutoFocus}
                className="fixed left-1/2 top-1/2 z-[60] flex w-[min(400px,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card bg-surface text-ink shadow-overlay outline-none"
            >
                <div className="flex h-14 shrink-0 items-center border-0 border-b border-solid border-line px-5">
                    <Dialog.Title className="m-0 flex-1 text-body-strong">{title}</Dialog.Title>
                    <Dialog.Close aria-label="Close" className="flex size-8 cursor-pointer items-center justify-center rounded-chip text-ink hover:bg-surface-muted">
                        <X size={18} aria-hidden="true" className={lucide}/>
                    </Dialog.Close>
                </div>
                <div id="confirm-dialog-desc" className="p-5 text-callout font-normal text-ink-muted">{children}</div>
                <div className="flex items-center justify-end gap-3 border-0 border-t border-solid border-line px-5 py-4">
                    <PhoneHQButton variant="secondary" className="px-5" onClick={() => onOpenChange(false)}>{cancelLabel}</PhoneHQButton>
                    <PhoneHQButton
                        variant={TONE[tone].variant}
                        className={`px-5 ${TONE[tone].className}`}
                        disabled={busy}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </PhoneHQButton>
                </div>
            </Dialog.Content>
        </Dialog.Portal>
    </Dialog.Root>
);
